<script setup lang="ts">
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {Euro, ExternalLink, Megaphone, Pill, ShieldCheck} from "@lucide/vue";

defineProps<{
    open: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
}>();

const rules = [
    {
        icon: Euro,
        title: 'Valeur modique',
        text: 'Le cadeau doit rester de faible valeur et sans lien avec le montant des achats ou des ordonnances du patient.',
    },
    {
        icon: Pill,
        title: 'Jamais de médicament',
        text: 'Aucun médicament, dispositif remboursable ou produit de prescription ne peut être offert — uniquement des produits de parapharmacie ou de confort.',
    },
    {
        icon: Megaphone,
        title: 'Pas de sollicitation de clientèle',
        text: 'Le geste est un remerciement relationnel : il ne doit pas être présenté comme une incitation à consommer ni faire l\'objet de publicité comparative.',
    },
    {
        icon: ShieldCheck,
        title: 'Consentement et données',
        text: 'Le patient doit avoir accepté d\'être contacté, pouvoir se désinscrire à tout moment, et ses données rester limitées à la relation officinale.',
    },
];
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-xl">
      <DialogHeader>
        <DialogTitle>Cadeau d'anniversaire et déontologie</DialogTitle>
      </DialogHeader>
      <p class="text-sm text-gray-dark leading-relaxed">
        Offrir un cadeau d'anniversaire à vos patients est possible, à condition de rester dans le
        cadre du code de la santé publique et du code de déontologie des pharmaciens.
      </p>
      <div class="flex flex-col gap-3">
        <div
          v-for="rule in rules"
          :key="rule.title"
          class="flex items-start gap-3 rounded-lg bg-primary-lightest p-3"
        >
          <component
            :is="rule.icon"
            class="mt-0.5 h-5 w-5 shrink-0 text-primary"
          />
          <div>
            <div class="text-sm font-semibold">
              {{ rule.title }}
            </div>
            <div class="text-sm text-gray-dark leading-relaxed">
              {{ rule.text }}
            </div>
          </div>
        </div>
      </div>
      <a
        href="https://www.ordre.pharmacien.fr/les-pharmaciens/le-code-de-deontologie"
        target="_blank"
        rel="noopener"
        class="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
      >
        <ExternalLink class="h-4 w-4" />Consulter le code de déontologie des pharmaciens
      </a>
      <p class="text-xs text-muted-foreground leading-relaxed">
        Cette synthèse est indicative et ne remplace pas l'avis de votre Ordre. En cas de doute sur
        un cadeau ou une opération, interrogez votre conseil régional avant de lancer la campagne.
      </p>
      <div class="flex justify-end">
        <Button @click="emit('update:open', false)">
          J'ai compris
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
